import React from 'react';
import { Loader2, Trash2, UserRound, X } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'doctor', label: 'Doctor' },
  { value: 'staff', label: 'Staff' },
  { value: 'consultant', label: 'External Consultant' },
];

export default function AvailabilityBoardModal({
  isOpen,
  dateLabel,
  loading,
  saving,
  deletingId,
  rows,
  draft,
  onDraftChange,
  onClose,
  onSave,
  onDelete,
  onEdit,
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-4xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
              Availability Board
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
              Doctor & Staff Availability
            </h2>
            <p className="mt-1.5 text-sm text-slate-500">{dateLabel}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-0 lg:grid-cols-[380px_minmax(0,1fr)]">
          <div className="border-b border-slate-100 bg-slate-50/70 p-6 lg:border-b-0 lg:border-r">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Add Today&apos;s Availability</h3>
                <p className="mt-1.5 text-sm text-slate-500">
                  Track doctor shifts, staff attendance, and consultant visits for the day.
                </p>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  value={draft.name}
                  onChange={(event) => onDraftChange('name', event.target.value)}
                  placeholder="Name"
                  className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-100"
                />

                <select
                  value={draft.role}
                  onChange={(event) => onDraftChange('role', event.target.value)}
                  className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-100"
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="time"
                    value={draft.startTime}
                    onChange={(event) => onDraftChange('startTime', event.target.value)}
                    className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-100"
                  />
                  <input
                    type="time"
                    value={draft.endTime}
                    onChange={(event) => onDraftChange('endTime', event.target.value)}
                    className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <textarea
                  value={draft.notes}
                  onChange={(event) => onDraftChange('notes', event.target.value)}
                  placeholder="Optional notes"
                  className="min-h-[96px] w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-100"
                />

                <label className="flex items-center gap-3 rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={draft.isActive}
                    onChange={(event) => onDraftChange('isActive', event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  Available today
                </label>
              </div>

              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-[0_12px_24px_-18px_rgba(15,23,42,0.35)] transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {draft.id ? 'Update Availability' : 'Save Availability'}
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Today&apos;s Team</h3>
                <p className="mt-1.5 text-sm text-slate-500">
                  These entries drive doctor selection during patient check-in.
                </p>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold text-slate-600">
                {rows.length} entries
              </span>
            </div>

            <div className="mt-5 space-y-3">
              {loading ? (
                <div className="flex min-h-[220px] items-center justify-center rounded-[22px] border border-dashed border-slate-200 bg-slate-50 text-sm font-medium text-slate-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
                  Loading availability...
                </div>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <div
                    key={row.id}
                    className="rounded-[22px] border border-slate-200 bg-white px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                          <UserRound className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-slate-900">{row.name}</p>
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                              {row.role}
                            </span>
                            <span
                              className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                row.isActive
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : 'border-slate-200 bg-slate-50 text-slate-500'
                              }`}
                            >
                              {row.isActive ? 'Available' : 'Inactive'}
                            </span>
                          </div>
                          <p className="mt-1.5 text-sm text-slate-500">
                            {row.startTime} - {row.endTime}
                          </p>
                          {row.notes ? (
                            <p className="mt-2 text-sm text-slate-500">{row.notes}</p>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(row.id)}
                          disabled={deletingId === row.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-60"
                        >
                          {deletingId === row.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-[22px] border border-dashed border-slate-200 bg-slate-50 px-6 py-14 text-center">
                  <p className="text-sm font-semibold text-slate-900">No availability added yet</p>
                  <p className="mt-1.5 text-sm text-slate-500">
                    Add at least one doctor or consultant so Check-in can reserve an available provider.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
